import React, { useMemo, useState } from "react";
import { ConversationsItems } from "../../../models/Conversations";
import { avatarColor, displayName, formatListDate, getInitials, getSpecialMessage, statusIcon } from "./utils";

interface ConversationListProps {
  conversations: ConversationsItems[] | null;
  loading: boolean;
  error: string | null;
  selectedId: number | null;
  onSelect: (conversation: ConversationsItems) => void;
  onRetry: () => void;
}

export const ConversationList: React.FC<ConversationListProps> = ({ conversations, loading, error, selectedId, onSelect, onRetry }) => {
  const [search, setSearch] = useState("");
  const [onlyUnread, setOnlyUnread] = useState(false);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (conversations ?? []).filter((conversation) => {
      if (onlyUnread && !(conversation.unread > 0)) return false;
      if (!term) return true;
      const haystack = [conversation.first_name, conversation.last_name, String(conversation.number ?? ""), conversation.last_message]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [conversations, search, onlyUnread]);

  const unreadTotal = (conversations ?? []).filter((c) => c.unread > 0).length;

  const preview = (conversation: ConversationsItems) => {
    const special = getSpecialMessage(conversation.last_message);
    if (special) return <><i className={`bi bi-${special.icon} me-1`} />{special.label}</>;
    if (conversation.last_message) return conversation.last_message;
    if (conversation.media_url) return <><i className="bi bi-paperclip me-1" />Archivo adjunto</>;
    return <span className="fst-italic">Sin mensajes</span>;
  };

  return (
    <div className="wa-sidebar-inner">
      <div className="wa-sidebar-header">
        <div className="wa-search">
          <i className="bi bi-search" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar nombre, número o mensaje"
            aria-label="Buscar conversaciones"
          />
        </div>
        <div className="wa-filters">
          <button type="button" className={`wa-chip ${!onlyUnread ? "wa-chip--active" : ""}`} onClick={() => setOnlyUnread(false)}>Todos</button>
          <button type="button" className={`wa-chip ${onlyUnread ? "wa-chip--active" : ""}`} onClick={() => setOnlyUnread(true)}>
            No leídos{unreadTotal > 0 ? ` (${unreadTotal})` : ""}
          </button>
        </div>
      </div>

      <div className="wa-list" role="list">
        {loading && !conversations && (
          <div className="wa-state"><span className="spinner-border spinner-border-sm me-2" />Cargando conversaciones…</div>
        )}

        {error && (
          <div className="wa-state text-danger">
            <div>{error}</div>
            <button type="button" className="btn btn-sm btn-outline-secondary mt-2" onClick={onRetry}>Reintentar</button>
          </div>
        )}

        {!loading && !error && conversations && filtered.length === 0 && (
          <div className="wa-state">
            <i className="bi bi-chat-square-text fs-2 d-block mb-2" />
            {search || onlyUnread ? "No hay conversaciones que coincidan." : "Aún no tienes conversaciones."}
          </div>
        )}

        {filtered.map((conversation) => {
          const name = displayName(conversation.first_name, conversation.last_name, conversation.number);
          const active = conversation.id === selectedId;
          const unread = conversation.unread > 0;
          const status = statusIcon(conversation.message_status);
          return (
            <button
              type="button"
              role="listitem"
              key={conversation.id}
              className={`wa-item ${active ? "wa-item--active" : ""}`}
              onClick={() => onSelect(conversation)}
              aria-current={active ? "true" : undefined}
            >
              <span className="wa-avatar" style={{ background: avatarColor(conversation.contact_id ?? conversation.number) }}>
                {getInitials(conversation.first_name || conversation.number)}
              </span>
              <span className="wa-item-body">
                <span className="wa-item-top">
                  <span className={`wa-item-name ${unread ? "fw-bold" : ""}`}>{name}</span>
                  <span className={`wa-item-date ${unread ? "wa-unread-color" : ""}`}>{formatListDate(conversation.message_date)}</span>
                </span>
                <span className="wa-item-bottom">
                  <span className="wa-item-preview">
                    {conversation.type === "outbound" && <i className={`bi bi-${status.icon} me-1 ${status.className}`} />}
                    {preview(conversation)}
                  </span>
                  {unread && <span className="wa-unread-badge" aria-label={`${conversation.unread} sin leer`}>{conversation.unread}</span>}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
